import { useMemo } from 'react';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import BusinessIcon from '@mui/icons-material/Business';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FlagIcon from '@mui/icons-material/Flag';
import GrassIcon from '@mui/icons-material/Grass';
import MapIcon from '@mui/icons-material/Map';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import QueueIcon from '@mui/icons-material/Queue';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ScienceIcon from '@mui/icons-material/Science';
import SearchIcon from '@mui/icons-material/Search';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import WorkIcon from '@mui/icons-material/Work';
import HexagonIcon from '@mui/icons-material/Hexagon';
import { usePermissions } from '../../hooks/use-permissions.js';
import { Hexbins } from './AdminHexbins';
import { Jobs } from './AdminJobs';
import { Lookup } from './AdminLookup';
import { Operations as FarmQA } from './farmqa/Operations';
import { Downloads } from './AdminDownloads';
import { FeatureFlags } from './AdminFeatureFlags';
import { Queues } from './AdminQueues';
import { Map } from './AdminMap';
import { GrowerExport } from './AdminGrowerExport';
import { Planting as PlantingUploads } from './uploads/Planting';
import { Yield as YieldUploads } from './uploads/Yield';
import { Lookup as UploadsLookup } from './uploads/Lookup';
import { Tokens as DeereTokens } from './deere/Tokens';
import { Subscriptions as DeereSubscriptions } from './deere/Subscriptions';
import { Collections as SoilCollections } from './soil/Collections';
import { Products as ZohoProducts } from './zoho/Products';
import GrowerManagement from '../grower-management/GrowerManagement';
import OrganizationManagement from '../organization-management/OrganizationManagement';
import UserManagement from '../user-management/UserManagement';

const allRoutes = [
  {
    label: 'Deere',
    icon: AgricultureIcon,
    children: [
      {
        path: '/deere/tokens',
        label: 'Access Tokens',
        component: DeereTokens,
        permission: 'ADMIN_DEERE',
      },
      {
        path: '/deere/subscriptions',
        label: 'Subscriptions',
        component: DeereSubscriptions,
        permission: 'ADMIN_DEERE',
      },
    ],
  },
  {
    path: '/downloads',
    label: 'Downloads',
    icon: CloudDownloadIcon,
    component: Downloads,
    permission: 'ADMIN_DOWNLOADS',
  },
  {
    path: '/farmqa',
    label: 'FarmQA',
    icon: ScienceIcon,
    component: FarmQA,
    permission: 'ADMIN_FARMQA',
  },
  {
    path: '/feature-flags',
    label: 'Feature Flags',
    icon: FlagIcon,
    component: FeatureFlags,
    permission: 'ADMIN_FEATURE_FLAGS',
  },
  {
    path: '/grower-export',
    label: 'Grower Export',
    icon: PersonIcon,
    component: GrowerExport,
    permission: 'ADMIN_GROWER_EXPORT',
  },
  {
    path: '/hexbins',
    label: 'Hexbins',
    icon: HexagonIcon,
    component: Hexbins,
    permission: 'ADMIN_HEXBINS',
  },
  {
    path: '/lookup',
    label: 'Lookup',
    icon: SearchIcon,
    component: Lookup,
    permission: 'ADMIN_LOOKUP',
  },
  {
    path: '/map',
    label: 'Map',
    icon: MapIcon,
    component: Map,
    permission: 'ADMIN_MAP',
  },
  {
    path: '/operations',
    label: 'Operations',
    icon: WorkIcon,
    component: GrowerManagement,
    permission: 'ADMIN_OPERATIONS',
  },
  {
    path: '/organizations',
    label: 'Organizations',
    icon: BusinessIcon,
    component: OrganizationManagement,
    permission: 'ADMIN_ORGANIZATIONS',
  },
  {
    path: '/queues',
    label: 'Queues',
    icon: QueueIcon,
    component: Queues,
    permission: 'ADMIN_QUEUES',
  },
  {
    path: '/jobs',
    label: 'Scheduled Jobs',
    icon: ScheduleIcon,
    component: Jobs,
    permission: 'ADMIN_QUEUES',
  },
  {
    label: 'Soil',
    icon: GrassIcon,
    children: [
      {
        path: '/soil/collections',
        label: 'Collections',
        component: SoilCollections,
        permission: 'ADMIN_SOIL',
      },
    ],
  },
  {
    label: 'Uploads',
    icon: CloudUploadIcon,
    children: [
      {
        path: '/uploads/lookup',
        label: 'Lookup',
        component: UploadsLookup,
        permission: 'ADMIN_UPLOADS',
      },
      {
        path: '/uploads/planting',
        label: 'Planting',
        component: PlantingUploads,
        permission: 'ADMIN_UPLOADS',
      },
      {
        path: '/uploads/yield',
        label: 'Yield',
        component: YieldUploads,
        permission: 'ADMIN_UPLOADS',
      },
    ],
  },
  {
    path: '/users',
    label: 'Users',
    icon: PeopleIcon,
    component: UserManagement,
    permission: 'ADMIN_USERS',
  },
  {
    label: 'Zoho',
    icon: ContactPageIcon,
    children: [
      {
        path: '/zoho/products',
        label: 'Products',
        component: ZohoProducts,
        permission: 'ADMIN_ZOHO',
      },
    ],
  },
];

const filterRoute = (route, userPermissions) => {
  if (route.children) {
    const children = route.children
      .map((child) => filterRoute(child, userPermissions))
      .filter(Boolean);
    return children.length > 0 ? { ...route, children } : null;
  }
  return userPermissions.includes(route.permission) ? route : null;
};

const filterRoutes = (routes, userPermissions) =>
  routes.map((route) => filterRoute(route, userPermissions)).filter(Boolean);

export function useRoutes() {
  const userPermissions = usePermissions();

  return useMemo(
    () => filterRoutes(allRoutes, userPermissions),
    [userPermissions],
  );
}
